import { useState, useCallback, memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { trackEvent } from '../utils/analytics';
import { generateFAQPageSchema } from '../utils/seo';
import { toast } from '../services/toastService';
import { createLogger } from '../utils/logger';

const logger = createLogger('FAQPanel');

/**
 * FAQPanel - Glassmorphic FAQ panel for immersive interface
 * Displays frequently asked questions with expandable answers
 */
const FAQPanel = memo(function FAQPanel() {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState(null);

  const faqs = [
    {
      question: t('faq.q1.question', 'How do I track food trucks near me?'),
      answer: t('faq.q1.answer', 'Open TrckIt and allow location access. The map will show all nearby mobile vendors based on recent community sightings. You can filter by type (food trucks, ice cream, coffee carts) and see check-in timestamps to know when a vendor was last spotted.')
    },
    {
      question: t('faq.q2.question', 'Is TrckIt free to use?'),
      answer: t('faq.q2.answer', 'Yes! TrckIt is completely free for everyone. Our mission is to support street food vendors and help communities discover them. No hidden fees, no premium tiers—just a platform built for vendors and food lovers.')
    },
    {
      question: t('faq.q3.question', 'How accurate are the vendor locations?'),
      answer: t('faq.q3.answer', 'Locations are community-reported through check-ins. Each sighting includes a timestamp so you can see how recent it is. The more active the community, the more accurate the locations. Most check-ins are within minutes to hours of the vendor being there.')
    },
    {
      question: t('faq.q4.question', 'Can I report a new vendor that\'s not on the map?'),
      answer: t('faq.q4.answer', 'Absolutely! Click the green "Spot Mode" button on the map, tap where the vendor is located, and fill in their details. Your contribution helps others discover great vendors. Add photos and descriptions to make the listing even better.')
    },
    {
      question: t('faq.q5.question', 'How do I check in a vendor location?'),
      answer: t('faq.q5.answer', 'Find the vendor on the map and click their marker to open their profile. From the detail panel, click \'Confirm Location\' to verify they\'re still there, or use Spot Mode to report them at a new location with photos and notes. Your check-in helps everyone know where vendors are right now.')
    },
    {
      question: t('faq.q6.question', 'What cities does TrckIt cover?'),
      answer: t('faq.q6.answer', 'TrckIt works anywhere! While we have active communities in many US cities, you can add vendors from any location worldwide. If you don\'t see vendors in your area, be the first to add them and build your local community.')
    },
    {
      question: t('faq.q7.question', 'Can vendors create their own profiles?'),
      answer: t('faq.q7.answer', 'Yes! Anyone can sign up and request to claim their listing through our contact form. Once approved, you get access to the Manage tab where you can update your listing details—name, description, contact info, and website—plus manage your official menu items. Every steward also gets a private support channel to communicate directly with our team.')
    },
    {
      question: t('faq.q8.question', 'How do I follow my favorite vendors?'),
      answer: t('faq.q8.answer', 'Click \'Add to Favorites & Join Channel\' to both bookmark a vendor and join their discussion channel, or click \'Join Discussion\' to only join the conversation without favoriting. Once in a channel, you can engage in real-time discussions and stay updated about that vendor.')
    },
    {
      question: t('faq.q9.question', 'Can I share my experience with vendors?'),
      answer: t('faq.q9.answer', 'Yes! When you spot a vendor or confirm a sighting, you can add a short note about your experience. These notes help other food lovers discover great vendors and stay informed about what\'s happening at different locations.')
    },
    {
      question: t('faq.q10.question', 'Is my data private?'),
      answer: t('faq.q10.answer', 'We take privacy seriously. Your location is only used to show nearby vendors and is never stored or shared. You control what information appears on your profile. You can delete your account at any time from your profile settings.')
    },
    {
      question: t('faq.q11.question', 'How do I contact support?'),
      answer: t('faq.q11.answer', 'Registered users have a dedicated support channel accessible from the Contact tab. This provides real-time messaging with our team, complete conversation history, and desktop notifications for new messages. Non-registered users can submit a contact form, but we recommend signing up for faster, more interactive support.')
    }
  ];

  // Generate slug from question text
  const generateSlug = useCallback((question) => {
    return question.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }, []);

  // Generate share link for FAQ
  const generateShareLink = useCallback((faq) => {
    const slug = generateSlug(faq.question);
    const baseUrl = window.location.origin;
    return `${baseUrl}/?faq=${slug}&lang=${t('app.languageCode', 'en')}`;
  }, [generateSlug, t]);

  const toggleExpand = useCallback((index, question) => {
    const willExpand = expandedId !== index;
    setExpandedId(willExpand ? index : null);
    
    // Track FAQ interaction
    trackEvent('FAQ', willExpand ? 'expand' : 'collapse', 'faq_tab', index, {
      question_index: index,
      question_preview: question?.substring(0, 50) || 'unknown',
      location: 'faq_tab'
    });
  }, [expandedId]);

  // Deep linking: Auto-expand FAQ from URL parameter
  // Note: Parent component (useImmersiveTabs) handles tab switching
  useEffect(() => {
    if (!faqs || faqs.length === 0) return;

    const params = new URLSearchParams(window.location.search);
    const faqSlug = params.get('faq');

    if (faqSlug) {
      // Find FAQ by slug
      const faqIndex = faqs.findIndex(faq => {
        const slug = generateSlug(faq.question);
        return slug === faqSlug;
      });

      if (faqIndex !== -1) {
        // Auto-expand the FAQ
        setExpandedId(faqIndex);
        
        // Track deep link navigation
        trackEvent('faq_deep_link', {
          question_index: faqIndex,
          question: faqs[faqIndex].question?.substring(0, 50)
        });

        // Scroll to FAQ after a brief delay
        setTimeout(() => {
          const element = document.getElementById(`faq-${faqIndex}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 500);
      }
    }
  }, [faqs, generateSlug]);

  // Add FAQ schema to page for SEO
  useEffect(() => {
    const schema = generateFAQPageSchema(faqs);
    if (schema) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      script.id = 'faq-schema';
      document.head.appendChild(script);
      
      return () => {
        const existing = document.getElementById('faq-schema');
        if (existing) {
          document.head.removeChild(existing);
        }
      };
    }
  }, [faqs]);

  return (
    <div className="space-y-2">
      {/* FAQ Items */}
      <div className="space-y-2">
        {faqs.map((faq, index) => {
          const handleShareFaq = async (e) => {
            if (e?.type === 'touchend') e.preventDefault()
            const shareLink = generateShareLink(faq)
            try {
              await navigator.clipboard.writeText(shareLink)
              trackEvent('faq_share', {
                question_index: index,
                question: faq.question?.substring(0, 50)
              })
              toast.success(t('faq.linkCopied', 'Link copied to clipboard!'))
            } catch (err) {
              logger.error('Failed to copy link', err)
              toast.error(t('faq.linkCopyFailed', 'Failed to copy link'))
            }
          }
          return (
          <div
            key={index}
            id={`faq-${index}`}
            className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200/50 overflow-hidden hover:shadow-md transition-all duration-200"
          >
            <button
              onClick={() => toggleExpand(index, faq.question)}
              onTouchEnd={(e) => { e.preventDefault(); toggleExpand(index, faq.question) }}
              className="w-full px-4 py-4 text-left flex items-start justify-between gap-3 hover:bg-gray-50/50 transition-colors"
              aria-expanded={expandedId === index}
            >
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 pr-2">
                  {faq.question}
                </h3>
              </div>
              {expandedId === index
                ? <ChevronUpIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                : <ChevronDownIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
              }
            </button>
            
            {expandedId === index && (
              <div className="px-4 pb-3 pt-1">
                <p className="text-sm text-gray-600 leading-relaxed mb-3">
                  {faq.answer}
                </p>
                
                {/* Share Button */}
                <button
                  onClick={handleShareFaq}
                  onTouchEnd={handleShareFaq}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white/90 backdrop-blur-sm border border-gray-200/50 hover:bg-white hover:border-gray-300 hover:shadow-md rounded-lg transition-all duration-200"
                  title={t('faq.shareLink', 'Copy share link')}
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  <span>{t('faq.share', 'Share')}</span>
                </button>
              </div>
            )}
          </div>
          )
        })}
      </div>

      {/* Help Footer */}
      <div className="mt-4 bg-orange-50/80 backdrop-blur-sm rounded-xl p-4 border border-orange-100/50">
        <p className="text-sm text-gray-700 text-center mb-2">
          {t('faq.needMoreHelp', 'Still have questions?')}
        </p>
        <p className="text-xs text-gray-600 text-center">
          {t('faq.contactPrompt', 'Can\'t find what you\'re looking for? We\'re here to help.')}
        </p>
      </div>

      {/* Built With / Open Source */}
      <div className="mt-4 border-t border-gray-200/60 pt-4">
        <p className="text-xs text-gray-400 text-center mb-3">
          {t('about.builtWith', 'Built with open source technology')}
        </p>
        <div className="flex flex-wrap justify-center gap-1.5 mb-4">
          {['React', 'Go', 'PostgreSQL', 'PostGIS', 'Redis', 'Leaflet', 'Tailwind', 'Docker'].map((tech) => (
            <span
              key={tech}
              className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full border border-gray-200/60"
            >
              {tech}
            </span>
          ))}
        </div>
        <div className="flex justify-center">
          <a
            href="https://github.com/trck-it"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm transition-all duration-200"
          >
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844a9.59 9.59 0 012.504.337c1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.02 10.02 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
            </svg>
            {t('about.starOnGithub', 'Star on GitHub')}
          </a>
        </div>
      </div>
    </div>
  );
})

export default FAQPanel
